import { useState } from "react";
import { Lock } from "lucide-react";

const SIGNAL_PHRASE = {
  happy: "feeling happy",
  calm: "feeling calm",
  playful: "feeling playful",
  sleepy: "feeling sleepy",
  shy: "feeling shy",
  curious: "feeling curious",
  need_hug: "wanted a hug",
  i_am_here: "saying I'm here",
};

export function selfCheckInLabel(signal) {
  return SIGNAL_PHRASE[signal] || signal;
}

/** A calm 4-digit keypad used to set or verify a PIN. */
export default function PinPad({
  onComplete,
  busy,
  error,
  label = "A quiet pause.",
  subtitle = "Enter the four-digit kiosk PIN to exit.",
}) {
  const [pin, setPin] = useState("");

  const tap = (digit) => {
    if (pin.length >= 4) return;
    const next = pin + digit;
    setPin(next);
    if (next.length === 4) {
      onComplete(next);
      // Clear after a small delay
      setTimeout(() => setPin(""), 400);
    }
  };

  const backspace = () => setPin((p) => p.slice(0, -1));

  return (
    <div data-testid="pin-pad" className="flex flex-col items-center">
      <Lock size={16} strokeWidth={1.5} className="text-[#8A9984]" />
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl italic text-[#2C2B29]">
        {label}
      </h2>
      <p className="mt-3 text-sm text-[#6A6863] font-light">
        {subtitle}
      </p>

      <div className="mt-8 flex gap-3" aria-label="PIN digits">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            data-testid={`pin-dot-${i}`}
            className={`h-3.5 w-3.5 rounded-full transition-colors duration-300 ${
              pin.length > i ? "bg-[#9BA995]" : "bg-[#2C2B29]/15"
            }`}
          />
        ))}
      </div>

      {error && (
        <p
          data-testid="pin-error"
          className="mt-5 text-sm text-[#2C2B29] italic font-serif"
        >
          {error}
        </p>
      )}

      <div className="mt-10 grid grid-cols-3 gap-3">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => tap(String(d))}
            disabled={busy}
            data-testid={`pin-key-${d}`}
            className="h-16 w-16 rounded-full bg-white border hairline text-2xl font-serif text-[#2C2B29] hover:bg-[#F2EFEA] transition-colors duration-300"
          >
            {d}
          </button>
        ))}
        <span aria-hidden />
        <button
          type="button"
          onClick={() => tap("0")}
          disabled={busy}
          data-testid="pin-key-0"
          className="h-16 w-16 rounded-full bg-white border hairline text-2xl font-serif text-[#2C2B29] hover:bg-[#F2EFEA] transition-colors duration-300"
        >
          0
        </button>
        <button
          type="button"
          onClick={backspace}
          disabled={busy || pin.length === 0}
          data-testid="pin-key-back"
          className="h-16 w-16 rounded-full text-sm text-[#6A6863] hover:text-[#2C2B29] transition-colors duration-300"
        >
          ←
        </button>
      </div>
    </div>
  );
}
